import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {useField, useFormikContext} from 'formik'

export interface MultiSelectComboboxOption {
  value: string
  label: string
}

export interface MultiSelectComboboxProps {
  name: string
  label?: string
  labelPosition?: 'top' | 'left'
  options: readonly MultiSelectComboboxOption[]
  placeholder?: string
  closeButtonLabel?: string
  disabled?: boolean
  wrapperClassName?: string
  maxListHeight?: number
}

/** Formik / API'den gelen tekil string, virgüllü string veya dizi değerlerini id dizisine çevirir */
function normalizeToStringArray(raw: unknown): string[] {
  if (raw == null) return []
  if (Array.isArray(raw)) {
    return raw
      .map((item) => {
        if (item == null) return null
        if (typeof item === 'object' && item !== null && 'id' in item) {
          return String((item as {id: unknown}).id)
        }
        return String(item)
      })
      .filter((x): x is string => x != null && x !== '')
  }
  if (typeof raw === 'string') {
    const t = raw.trim()
    if (!t) return []
    if (t.includes(',')) {
      return t
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
    }
    return [t]
  }
  return []
}

function fieldErrorMessage(error: unknown): string | undefined {
  if (error == null || error === false) return undefined
  if (typeof error === 'string') return error
  if (Array.isArray(error)) {
    const parts = error
      .map((e) => fieldErrorMessage(e))
      .filter((s): s is string => Boolean(s))
    return parts.length ? parts.join(', ') : undefined
  }
  if (typeof error === 'object') {
    for (const v of Object.values(error as Record<string, unknown>)) {
      const m = fieldErrorMessage(v)
      if (m) return m
    }
  }
  return undefined
}

function optionLabel(opt: MultiSelectComboboxOption): string {
  const {label, value} = opt
  if (typeof label === 'string') return label
  if (label != null && typeof label === 'object') {
    return String(value)
  }
  return String(value ?? '')
}

const MultiSelectCombobox: React.FC<MultiSelectComboboxProps> = ({
  name,
  label,
  labelPosition = 'top',
  options,
  placeholder = 'Seçiniz',
  closeButtonLabel = 'Kapat',
  disabled = false,
  wrapperClassName = '',
  maxListHeight = 220,
}) => {
  const [field, meta, helpers] = useField<unknown>({name})
  const {submitCount} = useFormikContext()
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)

  const selected = useMemo(() => normalizeToStringArray(field.value), [field.value])

  const closePanel = useCallback(() => {
    helpers.setTouched(true)
    setOpen(false)
  }, [helpers])

  const labelByValue = useCallback(
    (v: string) => options.find((o) => String(o.value) === v)?.label ?? v,
    [options]
  )

  const displayText =
    selected.length === 0
      ? placeholder
      : selected.map((v) => optionLabel({value: v, label: labelByValue(v)})).join(', ')

  const toggleValue = (value: string) => {
    const next = selected.includes(value)
      ? selected.filter((x) => x !== value)
      : [...selected, value]
    helpers.setValue(next)
  }

  useEffect(() => {
    if (!open) return

    const onDocMouseDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        closePanel()
      }
    }

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closePanel()
    }

    document.addEventListener('mousedown', onDocMouseDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open, closePanel])

  const renderLabel = () => {
    if (!label) return null
    return (
      <label className={`form-label fw-bold ${labelPosition === 'left' ? 'mb-0' : ''}`}>
        {label}
      </label>
    )
  }

  const errMsg = fieldErrorMessage(meta.error)
  const showInvalid = (meta.touched || submitCount > 0) && errMsg

  const control = (
    <div
      ref={rootRef}
      className='multi-select-combobox-anchor w-100 position-relative'
    >
      <div
        className={`multi-select-combobox__trigger form-select d-flex align-items-stretch p-0 ${
          showInvalid ? 'is-invalid' : ''
        }`}
      >
        <button
          type='button'
          className='multi-select-combobox__display'
          disabled={disabled}
          aria-expanded={open}
          aria-haspopup='listbox'
          onClick={() => {
            if (disabled) return
            if (open) closePanel()
            else setOpen(true)
          }}
        >
          <span
            className={
              selected.length === 0
                ? 'multi-select-combobox__placeholder'
                : 'multi-select-combobox__value'
            }
          >
            {displayText}
          </span>
        </button>
        <button
          type='button'
          className='multi-select-combobox__toggle'
          disabled={disabled}
          tabIndex={-1}
          aria-label={open ? 'Kapat' : 'Aç'}
          onClick={() => {
            if (disabled) return
            if (open) closePanel()
            else setOpen(true)
          }}
        >
          <i className='bi bi-chevron-down' aria-hidden />
        </button>
      </div>

      {showInvalid && <div className='invalid-feedback d-block'>{errMsg}</div>}

      {open && (
        <div className='multi-select-combobox__panel' role='listbox' aria-multiselectable>
          <div
            className='multi-select-combobox__list'
            style={{maxHeight: maxListHeight}}
          >
            {options.map((opt) => {
              const v = String(opt.value)
              const checked = selected.includes(v)
              return (
                <label key={v} className='multi-select-combobox__row'>
                  <input
                    type='checkbox'
                    className='multi-select-combobox__checkbox'
                    checked={checked}
                    onChange={() => toggleValue(v)}
                  />
                  <span className='multi-select-combobox__row-label'>{optionLabel(opt)}</span>
                </label>
              )
            })}
          </div>
          <div className='multi-select-combobox__footer'>
            <button type='button' className='multi-select-combobox__close-btn' onClick={closePanel}>
              {closeButtonLabel}
            </button>
          </div>
        </div>
      )}
    </div>
  )

  const fieldBlock = (
    <div className={labelPosition === 'left' && label ? 'flex-grow-1' : 'w-100'}>{control}</div>
  )

  if (labelPosition === 'left' && label) {
    return (
      <div className={`${wrapperClassName} h-100`}>
        <div className='d-flex align-items-start gap-3 h-100'>
          <div className='flex-shrink-0 pt-2'>{renderLabel()}</div>
          {fieldBlock}
        </div>
      </div>
    )
  }

  return (
    <div className={`${wrapperClassName} h-100`}>
      {renderLabel()}
      {fieldBlock}
    </div>
  )
}

export default MultiSelectCombobox
